import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { TourProvider as ReactourProvider, useTour } from '@reactour/tour';
import { TourConfig } from '../tours/types';
import { useDarkMode } from '../hooks/useDarkMode';

// Hook to detect mobile device
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth <= 768 : false
    );

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
};

interface TourContextType {
    registerTour: (tour: TourConfig) => void;
    unregisterTour: (tourId: string) => void;
    startTour: (tourId: string) => void;
    stopTour: () => void;
    isTourActive: boolean;
    availableTours: TourConfig[];
    activeTourId: string | null;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTourContext = () => {
    const context = useContext(TourContext);
    if (!context) {
        throw new Error('useTourContext must be used within a TourProvider');
    }
    return context;
};

interface TourProviderProps {
    children: React.ReactNode;
}

const TourContextProvider: React.FC<TourProviderProps> = ({ children }) => {
    const [registeredTours, setRegisteredTours] = useState<Map<string, TourConfig>>(new Map());
    const [activeTourId, setActiveTourId] = useState<string | null>(null);
    const { setIsOpen, setSteps, setCurrentStep, isOpen } = useTour();
    const prevIsOpenRef = React.useRef(isOpen);

    React.useEffect(() => {
        (window as any).__tourSetSteps__ = setSteps;
        return () => {
            delete (window as any).__tourSetSteps__;
        };
    }, [setSteps]);


    React.useEffect(() => {

        if (prevIsOpenRef.current && !isOpen && activeTourId) {

            if (activeTourId === 'main-dashboard') {
                console.log('[TOUR] Cleaning up main dashboard tour cards...');
                const controls = (window as any).assetTreeControls;
                if (controls && controls.removeTourCards) {
                    controls.removeTourCards();
                    console.log('[TOUR] Tour cards removed');
                }
                delete (window as any).tourCardGuids;
            }

            setActiveTourId(null);
        }

        prevIsOpenRef.current = isOpen;
    }, [isOpen, activeTourId]);

    const registerTour = useCallback((tour: TourConfig) => {
        setRegisteredTours(prev => {
            const newMap = new Map(prev);
            newMap.set(tour.id, tour);
            return newMap;
        });
    }, []);

    const unregisterTour = useCallback((tourId: string) => {
        setRegisteredTours(prev => {
            const newMap = new Map(prev);
            newMap.delete(tourId);
            return newMap;
        });
        if (activeTourId === tourId) {
            setActiveTourId(null);
            setIsOpen(false);
        }
    }, [activeTourId, setIsOpen]);

    const startTour = useCallback((tourId: string) => {
        const tour = registeredTours.get(tourId);
        if (tour && setSteps && setCurrentStep && setIsOpen) {
            setActiveTourId(tourId);

            const isMobile = window.innerWidth <= 768;
            const processedSteps = tour.steps.map(step => {
                const processed: any = { ...step };

                if (isMobile && step.mobilePosition) {
                    processed.position = step.mobilePosition;
                } else if (isMobile && !step.position) {
                    processed.position = 'center';
                }

                return processed;
            });

            setSteps(processedSteps as any);

            if (tourId === 'main-dashboard') {
                console.log('[TOUR] Opening drawer before starting main-dashboard tour...');
                const drawerControls = (window as any).assetDrawerControls;
                if (drawerControls && drawerControls.open) {
                    drawerControls.open();
                }

                setTimeout(() => {
                    setCurrentStep(0);
                    setIsOpen(true);
                }, 500);
            } else {
                setCurrentStep(0);
                setIsOpen(true);
            }
        } else if (!tour) {

        }
    }, [registeredTours, setSteps, setCurrentStep, setIsOpen]);

    const stopTour = useCallback(() => {
        if (activeTourId === 'main-dashboard') {
            const controls = (window as any).assetTreeControls;
            if (controls && controls.removeTourCards) {
                controls.removeTourCards();
            }
            delete (window as any).tourCardGuids;
        }

        setActiveTourId(null);
        setIsOpen(false);
    }, [activeTourId, setIsOpen]);

    const availableTours = useMemo(() => {
        return Array.from(registeredTours.values());
    }, [registeredTours]);

    const contextValue: TourContextType = {
        registerTour,
        unregisterTour,
        startTour,
        stopTour,
        isTourActive: isOpen,
        availableTours,
        activeTourId,
    };

    return (
        <TourContext.Provider value={contextValue}>
            {children}
        </TourContext.Provider>
    );
};


export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
    const isDarkMode = useDarkMode();
    const isMobile = useIsMobile();

    return (
        <ReactourProvider
            steps={[]}
            styles={{
                popover: (base) => ({
                    ...base,
                    backgroundColor: isDarkMode ? '#292929' : '#fff',
                    color: isDarkMode ? '#fff' : '#242424',
                    borderRadius: '8px',
                    margin: '0px',
                    boxShadow: isDarkMode
                        ? '0 4px 6px rgba(0, 0, 0, 0.3)'
                        : '0 4px 6px rgba(0, 0, 0, 0.1)',
                    border: isDarkMode ? '1px solid #404040' : 'none',
                    // Mobile-specific adjustments
                    maxWidth: isMobile ? '90vw' : '500px',
                    width: isMobile ? '90vw' : 'auto',
                    minWidth: isMobile ? 'auto' : '350px',
                    maxHeight: isMobile ? '60vh' : '80vh',
                    overflow: 'auto',
                    fontSize: isMobile ? '14px' : '16px',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                }),
                maskArea: (base) => ({
                    ...base,
                    rx: 8,
                }),
                badge: (base) => ({
                    ...base,
                    display: 'none',
                }),
                close: (base) => ({
                    ...base,
                    width: 22,
                    height: 22,
                    right: 8,
                    top: 8,
                    color: isDarkMode ? '#fff' : '#242424',
                }),
                dot: (base, state) => ({
                    ...base,
                    backgroundColor: state?.current
                        ? '#0078d4'
                        : isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                    display: isMobile ? 'none' : 'inline-block',
                }),
            }}
            padding={{ mask: 0, popover: [10, 10] }}
            scrollSmooth={true}
            position={isMobile ? 'center' : undefined}
            afterOpen={(target) => {
                if (target) {
                    setTimeout(() => {
                        target.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'center',
                        });
                    }, 100);
                }
            }}
            onClickMask={({ setIsOpen }) => setIsOpen(false)}
            prevButton={() => null}
            nextButton={({ currentStep, stepsLength, setIsOpen, setCurrentStep }) => {
                const isLastStep = currentStep === stepsLength - 1;

                return (
                    <button
                        onClick={() => {
                            if (isLastStep) {
                                setIsOpen(false);
                            } else {
                                setCurrentStep((s) => (s === stepsLength - 1 ? 0 : s + 1));
                            }
                        }}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#0078d4',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#106ebe';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#0078d4';
                        }}
                    >
                        {isLastStep ? 'Finish' : 'Next'}
                    </button>
                );
            }}
        >
            <TourContextProvider>{children}</TourContextProvider>
        </ReactourProvider>
    );
};
